"use client";

import { useEffect, useRef } from "react";
import { trackSectionViewed, isInitialized } from "@/lib/analytics";

/**
 * Hook that uses IntersectionObserver to fire a "Section Viewed" event
 * exactly once when the target element becomes at least 50% visible.
 *
 * @param sectionName  Human-readable name (e.g. "Hero Section")
 * @param sectionId    The DOM id of the section (e.g. "hero")
 */
export function useSectionTracking(
  sectionName: string,
  sectionId: string,
): React.RefObject<HTMLElement | null> {
  const ref = useRef<HTMLElement | null>(null);
  const tracked = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || tracked.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !tracked.current && isInitialized()) {
            tracked.current = true;
            trackSectionViewed(sectionName, sectionId);
            observer.disconnect();
          }
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [sectionName, sectionId]);

  return ref;
}
